import { memo, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { DCOLS, SEV_STYLE, CONF_COLOR, monoFont, tagSx, selectSx } from './DiConstants';

const PAGE_SIZE = 100;

const MultiValueCell = memo(function MultiValueCell({ field }) {
  const items = Array.isArray(field) ? field : (field ? [field] : []);

  if (!items.length || !items[0]?.value) {
    return <TableCell><Box component="span" sx={tagSx('#f3f4f6', '#9ca3af')}>—</Box></TableCell>;
  }

  return (
    <TableCell>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {items.map((item, i) => {
          const c = item.confidence === 'llm' ? CONF_COLOR.llm
            : item.confidence === 'inferred' ? CONF_COLOR.inferred
              : CONF_COLOR.default;
          return (
            <Box key={i} component="span" sx={tagSx(alpha(c, 0.08), c)}>
              {item.value}
            </Box>
          );
        })}
      </Box>
    </TableCell>
  );
});

const IssueRow = memo(function IssueRow({ r, gi, col }) {
  const sev = SEV_STYLE[r.severity] || SEV_STYLE.Low;
  return (
    <TableRow sx={{ '&:hover td': { backgroundColor: '#fafafa' } }}>
      <TableCell>
        <Typography sx={{ fontFamily: monoFont, fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
          {r.id}
        </Typography>
      </TableCell>
      <TableCell sx={{ maxWidth: 220 }}>
        <Tooltip title={r.summary} placement="top-start">
          <Typography sx={{ fontSize: 12, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
            {r.summary}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        {r.status
          ? <Box component="span" sx={tagSx('#f3f4f6', '#374151')}>{r.status}</Box>
          : <Box component="span" sx={tagSx('#f3f4f6', '#9ca3af')}>—</Box>
        }
      </TableCell>
      <MultiValueCell field={r.module} />
      <MultiValueCell field={r.platforms} />
      <MultiValueCell field={r.environments} />
      <MultiValueCell field={r.countries} />
      <TableCell><Box component="span" sx={tagSx(sev.bg, sev.color)}>{r.severity}</Box></TableCell>
      <TableCell>
        {col && (
          <Box component="span" sx={{ fontSize: 10, fontFamily: monoFont, fontWeight: 600, px: '7px', py: '2px', borderRadius: '5px', border: `1px solid ${alpha(col, 0.25)}`, backgroundColor: alpha(col, 0.08), color: col }}>
            G{gi + 1}
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
});

export const TableTab = memo(function TableTab({ classified, dupGroups }) {
  const [filters, setFilters] = useState({
    module: 'All', platform: 'All', env: 'All', country: 'All', severity: 'All', status: 'All', dups: false,
  });
  const [page, setPage] = useState(0);

  const dupMap = useMemo(() => {
    const m = {};
    (dupGroups || []).forEach((g, gi) => g.forEach(i => { m[i] = gi; }));
    return m;
  }, [dupGroups]);

  const opts = useMemo(() => {
    const platSet = new Set();
    const envSet  = new Set();
    const ctrySet = new Set();
    const statusSet = new Set();

    for (const r of classified) {
      (r.platforms    || []).forEach(p => p.value && platSet.add(p.value));
      (r.environments || []).forEach(e => e.value && envSet.add(e.value));
      (r.countries    || []).forEach(c => c.value && ctrySet.add(c.value));
      if (r.status) statusSet.add(r.status);
    }

    return {
      module:   ['All', ...new Set(classified.map(r => r.module?.value).filter(Boolean))].sort(),
      platform: ['All', ...[...platSet].sort()],
      env:      ['All', ...[...envSet].sort()],
      country:  ['All', ...[...ctrySet].sort()],
      severity: ['All', 'Critical', 'High', 'Medium', 'Low'],
      status:   ['All', ...[...statusSet].sort()],
    };
  }, [classified]);

  const filtered = useMemo(() => {
    const result = [];
    for (let i = 0; i < classified.length; i++) {
      const r = classified[i];
      if (filters.module !== 'All' && r.module?.value !== filters.module) continue;
      if (filters.platform !== 'All' && !(r.platforms || []).some(p => p.value === filters.platform)) continue;
      if (filters.env !== 'All' && !(r.environments || []).some(e => e.value === filters.env)) continue;
      if (filters.country !== 'All' && !(r.countries || []).some(c => c.value === filters.country)) continue;
      if (filters.severity !== 'All' && r.severity !== filters.severity) continue;
      if (filters.status !== 'All' && r.status !== filters.status) continue;
      if (filters.dups && dupMap[i] === undefined) continue;
      result.push({ r, origIdx: i });
    }
    return result;
  }, [classified, dupMap, filters]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageSlice  = useMemo(
    () => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filtered, page],
  );

  const setF = (key) => (e) => {
    setPage(0);
    setFilters(f => ({ ...f, [key]: e.target.value }));
  };

  const handleDups = (e) => {
    setPage(0);
    setFilters(f => ({ ...f, dups: e.target.checked }));
  };

  return (
    <Box>
      <Box sx={{ mb: 2.25 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', color: '#9ca3af', mb: 0.5 }}>
          Issues
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.4px', color: '#111827' }}>
          All classified issues
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.75 }}>
        {[
          { key: 'module',   label: 'Module',   options: opts.module },
          { key: 'platform', label: 'Platform', options: opts.platform },
          { key: 'env',      label: 'Env',      options: opts.env },
          { key: 'country',  label: 'Country',  options: opts.country },
          { key: 'severity', label: 'Severity', options: opts.severity },
          { key: 'status',   label: 'Status',   options: opts.status },
        ].map(({ key, label, options }) => (
          <FormControl key={key} size="small" sx={selectSx}>
            <InputLabel sx={{ fontSize: 12 }}>{label}</InputLabel>
            <Select label={label} value={filters[key]} onChange={setF(key)}>
              {options.map(o => <MenuItem key={o} value={o} sx={{ fontSize: 12 }}>{o}</MenuItem>)}
            </Select>
          </FormControl>
        ))}
        <FormControlLabel
          control={
            <Checkbox
              size="small" checked={filters.dups} onChange={handleDups}
              sx={{ color: '#d4d8e2', '&.Mui-checked': { color: '#111827' } }}
            />
          }
          label={<Typography sx={{ fontSize: 12, color: '#374151' }}>Duplicates only</Typography>}
          sx={{ m: 0, border: '1px solid #e8eaef', borderRadius: '8px', px: 1.25, py: 0.875, backgroundColor: '#ffffff' }}
        />
        <Typography sx={{ fontFamily: monoFont, fontSize: 12, color: '#9ca3af', ml: 'auto' }}>
          {filtered.length} of {classified.length}
        </Typography>
      </Box>

      {/* Table */}
      <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        <Box sx={{overflowX:'auto'}}>
          <Table size="large" sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                {['ID', 'Summary', 'Status', 'Module', 'Platform', 'Env', 'Country', 'Severity', 'Dup'].map(h => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!pageSlice.length ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: 'center', color: '#9ca3af', py: 5, fontSize: 13 }}>
                    No issues match the current filters.
                  </TableCell>
                </TableRow>
              ) : pageSlice.map(({ r, origIdx }) => (
                <IssueRow
                  key={origIdx}
                  r={r}
                  gi={dupMap[origIdx]}
                  col={dupMap[origIdx] !== undefined ? DCOLS[dupMap[origIdx] % DCOLS.length] : null}
                />
              ))}
            </TableBody>
          </Table>
        </Box>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, px: 2, py: 1.25, borderTop: '1px solid #e8eaef' }}>
            <Typography sx={{ fontFamily: monoFont, fontSize: 12, color: '#9ca3af' }}>
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
            </Typography>
            <IconButton
              size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}
              sx={{ color: '#374151', '&.Mui-disabled': { color: '#d4d8e2' } }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
              sx={{ color: '#374151', '&.Mui-disabled': { color: '#d4d8e2' } }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Paper>
    </Box>
  );
});